/** Attendance Setup is a non-test flow; Event type is Others only. */
export const EVENT_TYPES: readonly string[] = ["Others"];

function envInt(key: string, fallback: number): number {
  const raw = process.env[key];
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export const DEFAULT_TIMER_SECONDS = envInt(
  "REACT_APP_ATTENDANCE_DEFAULT_TIMER_SECONDS",
  15,
);
export const EXTEND_SECONDS = envInt("REACT_APP_ATTENDANCE_EXTEND_SECONDS", 15);
export const MAX_ATTENDANCE_ROUNDS = envInt("REACT_APP_ATTENDANCE_MAX_ROUNDS", 3);
export const MAX_STUDENTS_PER_GROUP = envInt(
  "REACT_APP_ATTENDANCE_MAX_STUDENTS_PER_GROUP",
  20,
);
export const MIN_GROUPS = envInt("REACT_APP_ATTENDANCE_MIN_GROUPS", 3);
export const MAX_GROUPS = envInt("REACT_APP_ATTENDANCE_MAX_GROUPS", 40);

export function resolveEffectiveMinGroups(
  totalStudents: number,
  minGroups: number,
): number {
  if (totalStudents <= 0) return 0;
  if (totalStudents <= 3) return totalStudents;
  return minGroups;
}

export function resolveEffectiveMaxGroups(
  maxGroups: number,
  activeImageCount: number,
): number {
  if (activeImageCount <= 0) return 0;
  return Math.min(maxGroups, activeImageCount);
}

export function computeGroupSplit(
  totalStudents: number,
  maxPerGroup = MAX_STUDENTS_PER_GROUP,
  minGroups = MIN_GROUPS,
  maxGroups = MAX_GROUPS,
  activeImageCount?: number,
): number[] {
  if (totalStudents <= 0) return [];

  const effectiveMaxGroups =
    activeImageCount !== undefined
      ? resolveEffectiveMaxGroups(maxGroups, activeImageCount)
      : maxGroups;
  const neededForCap = Math.ceil(totalStudents / maxPerGroup);
  const effectiveMinGroups = resolveEffectiveMinGroups(totalStudents, minGroups);
  const groupCount = Math.min(
    effectiveMaxGroups,
    Math.max(effectiveMinGroups, neededForCap),
  );
  const base = Math.floor(totalStudents / groupCount);
  const remainder = totalStudents % groupCount;
  const smallerGroupCount = groupCount - remainder;

  return Array.from({ length: groupCount }, (_, i) =>
    i < smallerGroupCount ? base : base + 1,
  );
}

export function describeGroupSplitLimitError(
  totalStudents: number,
  maxPerGroup: number,
  maxGroups: number,
  activeImageCount: number,
): string | null {
  if (totalStudents <= 0) return "No students found for the selected batch(es).";

  const effectiveMaxGroups = resolveEffectiveMaxGroups(maxGroups, activeImageCount);
  const neededForCap = Math.ceil(totalStudents / maxPerGroup);
  const maxCapacity = effectiveMaxGroups * maxPerGroup;

  if (neededForCap > effectiveMaxGroups) {
    return (
      `Cannot split ${totalStudents} students: need ${neededForCap} groups but maximum is ` +
      `${effectiveMaxGroups} (${activeImageCount} unique patterns available, ` +
      `max capacity ${maxCapacity} students). Reduce batch selection or split into multiple sessions.`
    );
  }

  return null;
}

export function formatCurrentDate(): string {
  return new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function getCurrentTime(): string {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}
